import { createIsomorphicFn } from '@tanstack/react-start';

export const PREVIEW_COOKIE_NAME = 'preview';

const normalizeBooleanValue = (value: string | null | undefined) => value?.trim().toLowerCase();

const getClientCookieValue = (name: string) => {
    return document.cookie
        .split('; ')
        .find((entry) => entry.startsWith(`${name}=`))
        ?.split('=')[1];
};

const getServerIsSecureRequest = createIsomorphicFn()
    .server(async () => {
        const { getRequestHeader } = await import('@tanstack/react-start/server');
        return getRequestHeader('x-forwarded-proto')?.toLowerCase() === 'https';
    })
    .client(() => window.location.protocol === 'https:');

const getPreviewCookieAttributes = async () => {
    const isSecureRequest = await getServerIsSecureRequest();
    const secure = isSecureRequest ? '; Secure' : '';
    return `path=/; SameSite=Lax${secure}`;
};

export const parsePreviewSearchValue = (value: unknown): boolean => {
    if (typeof value !== 'string') return false;
    return value.trim().toLowerCase() === 'true';
};

export const isPreviewCookieEnabled = (value: string | null | undefined): boolean => {
    return normalizeBooleanValue(value) === 'true';
};

export const getPreviewCookie = createIsomorphicFn()
    .server(async () => {
        const { getCookie } = await import('@tanstack/react-start/server');
        return getCookie(PREVIEW_COOKIE_NAME);
    })
    .client(() => {
        return getClientCookieValue(PREVIEW_COOKIE_NAME);
    });

export const setPreviewCookie = createIsomorphicFn()
    .server(async () => {
        const { setCookie } = await import('@tanstack/react-start/server');
        const isSecureRequest = await getServerIsSecureRequest();

        setCookie(PREVIEW_COOKIE_NAME, 'true', {
            path: '/',
            httpOnly: false,
            secure: isSecureRequest,
            sameSite: 'lax',
        });
    })
    .client(async () => {
        const attributes = await getPreviewCookieAttributes();
        document.cookie = `${PREVIEW_COOKIE_NAME}=true; ${attributes}`;
    });

export const clearPreviewCookie = createIsomorphicFn()
    .server(async () => {
        const { deleteCookie } = await import('@tanstack/react-start/server');

        deleteCookie(PREVIEW_COOKIE_NAME, {
            path: '/',
        });
    })
    .client(async () => {
        const attributes = await getPreviewCookieAttributes();
        document.cookie = `${PREVIEW_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; ${attributes}`;
    });
